import { useEffect, useState, useMemo } from "react";
import backArrow from "../../img/backArrow.png";
import { withRouter, Link, useParams, useLocation } from "react-router-dom";
import "./ProductDetails.scss";
import { handleLeftDrawer } from "../../actions/actionCreators";
import { useDispatch, useSelector, RootStateOrAny } from "react-redux";
import BottomDrawer from "../BottomDrawer/BottomDrawer";
import { getAllFolders } from "../../actions/folders-actions/actions";
import {
  getFilterAttributes,
  getProduct,
  getProductsFiltered,
  getType,
} from "../../actions/products-actions/actions";
import { handleSearchScreen } from "../../actions/search-actions/actions";
import { history } from "../../index";
import { v4 as uuidv4 } from "uuid";

import ProductSkeleton from "./ProductSkeleton";
import ProductHeader from "./ProductHeader";
import DenimProduct from "./DenimProduct";
import KnitProduct from "./KnitProduct";
import ProductFooter from "./ProductFooter";
import ProductImage from "./ProductImage";
import axiosInstance from "../../config/axios-instance";
import { Breadcrumb } from "antd";
import MixProduct from "./MixProduct";

export interface IProductDetailsProps {}

const ProductDetails = (props: IProductDetailsProps) => {
  const [totalLook, setTotalLook] = useState<any>(null);
  const [isLoadingTotalLook, setIsLoadingTotalLook] = useState(false);
  const [error, setError] = useState(null);
  const [width, setWidth] = useState(window.innerWidth);
  const [editMode, setEditMode] = useState(false);
  const [mode, setMode] = useState("add_to_collection");
  const [visible, setVisible] = useState(false);
  
  const folders = useSelector((state: RootStateOrAny) => state.folders);
  const drawers = useSelector((state: RootStateOrAny) => state.drawers);
  const { nextProduct, isLoading, selectedProduct } =
    useSelector((state: RootStateOrAny) => state.products);

  const dispatch = useDispatch();
  const params: any = useParams();
  const location = useLocation();

  // Resize handler
  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Parallel Fetching: Track Visit & Get Total Look & Redux Actions
  useEffect(() => {
    const fetchPageData = async () => {
      const token = localStorage.getItem("token");
      setIsLoadingTotalLook(true);

      // Dispatch Redux actions immediately
      dispatch(getAllFolders());
      dispatch(getProduct(params.productId));

      try {
        await Promise.all([
          // Track Visit
          axiosInstance({
            method: "post",
            url: `products/track-visit`,
            data: { product_id: params.productId },
            headers: { Authorization: `Bearer ${token}` },
          }),
          // Get Total Look
          axiosInstance({
            method: "get",
            url: `/products/${params.productId}/total-look`,
            headers: { Authorization: `Bearer ${token}` },
          }).then((res) => {
            if (res && res.data.data) {
              setTotalLook(res.data.data);
            }
          }),
        ]);
      } catch (err: any) {
        console.error("Error loading product details data", err);
        setError(err);
      } finally {
        setIsLoadingTotalLook(false);
      }
    };

    if (params.productId) {
      fetchPageData();
    }
  }, [params.productId, dispatch]);

  // Derived state for similar products using useMemo (prevents extra render)
  const similarProducts = useMemo(() => {
    if (!selectedProduct) return [];
    return selectedProduct?.fabrics?.flatMap((obj: any) =>
      obj.products.filter((product: any) => product.id !== selectedProduct.id)
    ) || [];
  }, [selectedProduct]);

  // Dependent Redux Dispatches
  useEffect(() => {
    if (selectedProduct) {
      dispatch(
        getProductsFiltered(
          selectedProduct?.fabric_type,
          selectedProduct?.categories?.[0]?.id,
          selectedProduct?.gender_id
        )
      );
      dispatch(getFilterAttributes(selectedProduct.fabric_type, "17", "1"));
    }
  }, [selectedProduct, dispatch]);


  const oneProduct = selectedProduct; // Alias for cleaner JSX below without refactoring everything

  return isLoading ? (
    <ProductSkeleton oneProduct={oneProduct} />
  ) : (
    <div className="product_details_block container-fluid">
      <div className="product_details_contanier row">
        <div className="product_images_wrapper col-6 ">
          <ProductImage
            oneProduct={oneProduct}
            width={width}
            setVisible={setVisible}
            visible={visible}
          />
        </div>
        <div className="product_details_infos_scroll col-6">
          <div
            className="product_details_infos"
            style={{ paddingTop: editMode ? "20px" : "0px" }}
          >
            {oneProduct && oneProduct.fabric_type === "knit" ? (
              <KnitProduct
                oneProduct={oneProduct}
                editMode={editMode}
                setEditMode={setEditMode}
                // Removed key={uuidv4()} to prevent re-mounting
                totalLook={totalLook}
                similarProducts={similarProducts}
                isLoadingTotalLook={isLoadingTotalLook}
                isLoadingSimilar={false} 
              />
            ) : (oneProduct && oneProduct.fabric_type === "denim") ?  (
              <DenimProduct
                oneProduct={oneProduct}
                editMode={editMode}
                setEditMode={setEditMode}
                // Removed key={uuidv4()} to prevent re-mounting
                totalLook={totalLook}
                similarProducts={similarProducts}
                isLoadingTotalLook={isLoadingTotalLook}
                isLoadingSimilar={false}
              />
            ): (
              <MixProduct
                  oneProduct={oneProduct}
                  editMode={editMode}
                  setEditMode={setEditMode}
                  // Removed key={uuidv4()} to prevent re-mounting
                  totalLook={totalLook}
                  similarProducts={similarProducts}
                  isLoadingTotalLook={isLoadingTotalLook}
                  isLoadingSimilar={false}
              />
            )}
          </div>
          {!editMode && (
            <ProductFooter
              isLoadingNext={isLoading}
              width={width}
              oneProduct={oneProduct}
            />
          )}
        </div>
      </div>

      {drawers.openBottom && (
        <BottomDrawer folders={folders} mode={mode} setMode={setMode} />
      )}
    </div>
  );
};

export default withRouter(ProductDetails);
